import { defineStore } from 'pinia';
import { debugLog } from '@/helpers/loggingFunctions.js';
import { fetchEndpoint } from '@/helpers/storeFunctions.js';
import { parseFormData } from './applicationStore';

export const useUserStore = defineStore('userStore', {
    state: () => ({
        profile: {
            id: null,
            first_name: '',
            last_name: '',
            email: '',
            phone_number: '',
            email_confirmed: false
        },
        applications: []
    }),

    getters: {
        formattedPhoneNumber(state) {
            return state.profile.phone_number
                ? String(state.profile.phone_number).replace(/(\d{3})(\d{3})(\d{4})/, '$1.$2.$3')
                : '';
        }
    },

    actions: {
        unformatPhoneNumber(value) {
            return String(value).replace(/\D/g, '');
        },

        userHasConfirmedEmail() {
            return this.profile.email_confirmed;
        },

        setUser(profile) {
            debugLog('userStore.setUser triggered');

            this.profile = profile;
        },

        confirmEmail() {
            this.profile.email_confirmed = true;
        },

        removeApplicationFromList(id) {
            this.applications = this.applications.filter((application) => application.id !== id);
        },

        async getProfile() {
            debugLog('getProfile triggered');

            return await fetchEndpoint(this, 'api/users/me', 'get', null, 'profile');
        },

        async updateProfile(data) {
            debugLog('updateProfile triggered');

            return await fetchEndpoint(this, 'api/users/me', 'patch', data, 'profile');
        },

        async deleteUser(id) {
            try {
                await fetchEndpoint(this, `api/users/${id}`, 'delete', null, null);
            } catch (error) {
                return error;
            }
        },

        async checkDuplicateEmail(email) {
            try {
                return await fetchEndpoint(
                    this,
                    `api/users/check-email`,
                    'post',
                    { email: email },
                    null
                );
            } catch (error) {
                return error;
            }
        },

        async getUserApplications() {
            debugLog('userStore.getUserApplications triggered');

            const getUserPrequalsResponse = await fetchEndpoint(
                this,
                'api/quick-prequals',
                'get',
                null,
                'applications'
            );

            const { data } = getUserPrequalsResponse;

            return data;
        },

        async verifyConfirmationCode(data, updateProfile = false) {
            debugLog('userStore.verifyCode triggered');

            const verifyResponse = await fetchEndpoint(
                this,
                'api/verify-confirmation-code',
                'post',
                data
            );

            const { status } = verifyResponse;

            if (status === 'success' && updateProfile) {
                this.confirmEmail();
            }

            return verifyResponse;
        },

        async resendCode(data) {
            debugLog('userStore.resendCode triggered');

            return await fetchEndpoint(this, 'api/resend-confirmation-code', 'post', data);
        },

        async verifySmsConfirmationCode(data, updateProfile = false) {
            debugLog('userStore.verifySmsCode triggered');

            const verifyResponse = await fetchEndpoint(this, 'api/sms-code/verify', 'post', data);

            const { status } = verifyResponse;

            if (status === 'success' && updateProfile) {
                this.confirmEmail();
            }

            return verifyResponse;
        },

        async resendSmsCode(data) {
            debugLog('userStore.resendSmsCode triggered');

            return await fetchEndpoint(this, 'api/sms-code/send', 'post', data);
        },

        saveToLocalStorage() {
            try {
                const parsedData = parseFormData(this.$state);
                localStorage.setItem('userStore', JSON.stringify(parsedData));
            } catch (error) {
                console.error('Error saving to localStorage:', error);
            }
        },

        loadFromLocalStorage() {
            try {
                const savedState = localStorage.getItem('userStore');
                if (savedState) {
                    this.$patch(parseFormData(JSON.parse(savedState)));
                }
            } catch (error) {
                console.error('Error loading from localStorage:', error);
            }
        },

        clearStore() {
            localStorage.removeItem('userStore');

            this.$reset();
        }
    }
});
